import { useState, useEffect } from "react";
import S from "../styles/styles";
import { PriorityBadge, StatusBadge, Field } from "./UI";
import { STATUS_STYLES, GST_RATE } from "../data/seed";

const DEFAULT_MARKUP = 15; // 15% default markup

const fmtC = (n) => n ? `$${parseFloat(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "$0.00";

const TicketDrawer = ({
  ticket, properties, contractors = [], owners = [], users = [],
  onClose, onUpdateStatus, onUpdateTicket, onPaymentCreated,
  onGetComments, onAddComment, currentUser,
}) => {
  const [payResult,    setPayResult]    = useState(null);
  const [jobAmount,    setJobAmount]    = useState(ticket?.jobAmount || "");
  const [contractorId, setContractorId] = useState(ticket?.contractorId || "");
  const [showBilling,  setShowBilling]  = useState(false);
  const [gstAmount,    setGstAmount]    = useState(ticket?.gstAmount || "");
  const [markupPct,    setMarkupPct]    = useState(DEFAULT_MARKUP);
  const [billableTo,   setBillableTo]   = useState("owner");

  // Look up renter email from users list
  // Load comments when ticket opens
  useEffect(() => {
    if (ticket?.id && onGetComments) {
      onGetComments(ticket.id).then(setComments);
    }
  }, [ticket?.id]);

  const renterUser  = users.find((u) =>
    (ticket?.renterId && u.id === ticket.renterId) ||
    (ticket?.renter   && u.name?.toLowerCase() === ticket.renter?.toLowerCase()) ||
    (ticket?.renter   && u.email?.toLowerCase() === ticket.renter?.toLowerCase())
  );
  const renterEmail = renterUser?.email || "";
  const renterName  = renterUser?.name || ticket?.renter || "Tenant";
  const propObj     = properties.find((p) => p.id === ticket?.propertyId);
  const propAddress = propObj?.address || propObj?.name || "";
  const [billNote,     setBillNote]     = useState("");
  const [comments,     setComments]     = useState([]);
  const isMobile = window.innerWidth < 768;
  const [newComment,   setNewComment]   = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [billed,       setBilled]       = useState(ticket?.billed || false);

  if (!ticket) return null;

  const property   = properties.find((p) => p.id === ticket.propertyId);
  const contractor = contractors.find((c) => c.id === parseInt(contractorId));
  const owner      = owners.find((o) => o.propertyId === ticket.propertyId);

  const baseAmount    = parseFloat(jobAmount) || 0;
  const gst           = parseFloat(gstAmount) || 0;
  const markupAmt     = parseFloat((baseAmount * markupPct / 100).toFixed(2));
  const billableTotal = parseFloat((baseAmount + gst + markupAmt).toFixed(2));

  const canPay = ticket.status === "RESOLVED" && contractor && baseAmount > 0 && !ticket.paid;

  const handleAssign = () => {
    const c = contractors.find((c) => c.id === parseInt(contractorId));
    onUpdateTicket(ticket.id, {
      contractorId: parseInt(contractorId) || null,
      assignee:     c?.name || null,
      jobAmount:    parseFloat(jobAmount) || null,
    });
  };

  const handlePay = () => {
    if (!canPay) return;
    onUpdateTicket(ticket.id, { paid: true, jobAmount: parseFloat(jobAmount) });
    onPaymentCreated?.({
      type:         "contractor_payout",
      ticketId:     ticket.id,
      contractorId: contractor.id,
      propertyId:   ticket.propertyId,
      amount:       parseFloat(jobAmount),
      status:       "paid",
      date:         new Date().toISOString().split("T")[0],
      notes:        ticket.title,
    });
    setPayResult("success");
  };

  const handleGenerateBill = () => {
    onUpdateTicket(ticket.id, {
      gstAmount:    gst,
      markupPct,
      markupAmount: markupAmt,
      billableTotal,
      billableTo,
      billNote,
      billed:       true,
    });
    setBilled(true);

    const billText = [
      "BILLABLE CHARGE",
      "----------------",
      `Property: ${property?.address || "-"}`,
      `Unit: ${ticket.unit || "-"}`,
      `Work: ${ticket.title}`,
      `Contractor: ${contractor?.name || "-"}`,
      contractor?.gstNumber ? `Contractor GST#: ${contractor.gstNumber}` : "",
      "",
      `Contractor Invoice: ${fmtC(baseAmount)}`,
      `GST (contractor): ${fmtC(gst)}`,
      `Management Markup (${markupPct}%): ${fmtC(markupAmt)}`,
      `Total Billable: ${fmtC(billableTotal)}`,
      "",
      `Bill To: ${billableTo === "owner" ? (owner?.name || "Property Owner") : (ticket.renter || "Tenant")}`,
      billNote ? `Note: ${billNote}` : "",
    ].filter(Boolean).join("\n");

    navigator.clipboard.writeText(billText).catch(() => {});
    alert(`Bill of ${fmtC(billableTotal)} generated and copied to clipboard.\n\n${billableTo === "owner" ? "Add as a line item in the Invoicing tab." : "Send notice to renter via BC Forms tab."}`);
  };

  return (
    <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.drawer, paddingBottom: isMobile ? 80 : 24 }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, color: "#5A5570", fontWeight: 700, marginBottom: 4 }}>TICKET #{ticket.id}</div>
            <div style={S.drawerTitle}>{ticket.title}</div>
          </div>
          <button style={{ ...S.btnSmall, background: "#ffffff10", color: "#F0EDE8" }} onClick={onClose}>X</button>
        </div>

        {/* Badges */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
          <PriorityBadge priority={ticket.priority} />
          <StatusBadge   status={ticket.status} />
          {ticket.paid   && <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, background: "#22AA5518", color: "#22AA55" }}>Paid</span>}
          {ticket.billed && <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, background: "#F5C84218", color: "#F5C842" }}>Billed</span>}
        </div>

        {/* Message Renter Button */}
        <a
          href={`mailto:${renterEmail}?subject=Re: Your Maintenance Request - ${ticket.title}&body=Hi ${renterName},%0D%0A%0D%0ARegarding your maintenance request "${ticket.title}" at ${propAddress}, Unit ${ticket.unit}:%0D%0A%0D%0A[Your message here]%0D%0A%0D%0AThank you,%0D%0APropDesk Property Management`}
          style={{ ...S.btnPrimary, display: "inline-block", marginBottom: 20, fontSize: 12, padding: "8px 16px", textDecoration: "none", background: "#2A7BE8" }}>
          ✉️ Message Renter {renterEmail ? `(${renterEmail})` : ""}
        </a>

        {/* Details */}
        {[
          { label: "Renter",    value: ticket.renter    },
          { label: "Unit",      value: ticket.unit       },
          { label: "Property",  value: property?.address },
          { label: "Category",  value: ticket.category   },
          { label: "Submitted", value: ticket.date        },
        ].map((row) => (
          <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #ffffff06" }}>
            <span style={{ fontSize: 12, color: "#5A5570", fontWeight: 700 }}>{row.label}</span>
            <span style={{ fontSize: 13, color: "#F0EDE8", textAlign: "right" }}>{row.value}</span>
          </div>
        ))}

        {/* Description */}
        <div style={{ margin: "16px 0" }}>
          <div style={S.label}>Description</div>
          <div style={{ fontSize: 13, color: "#9993A8", lineHeight: 1.6, background: "#0F0F14", borderRadius: 8, padding: "12px 14px" }}>
            {ticket.description || "No description provided."}
          </div>
        </div>

        {/* Photos */}
        {ticket.photos?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={S.label}>Photos ({ticket.photos.length})</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
              {ticket.photos.map((photo, i) => (
                <a key={i} href={photo.url} target="_blank" rel="noopener noreferrer">
                  <img src={photo.url} alt={photo.name} style={{ ...S.photoThumb, width: 80, height: 80 }} />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Contractor Assignment */}
        <div style={{ background: "#0F0F14", borderRadius: 10, padding: "14px", marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "#9993A8", fontWeight: 700, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Contractor Assignment
          </div>
          <Field label="Assign Contractor">
            <select style={S.select} value={contractorId} onChange={(e) => setContractorId(e.target.value)}>
              <option value="">Unassigned</option>
              {contractors.map((c) => (
                <option key={c.id} value={c.id}>{c.name} - {c.trade}</option>
              ))}
            </select>
          </Field>
          <Field label="Contractor Invoice Amount (CAD)">
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9993A8", fontSize: 13 }}>$</span>
              <input style={{ ...S.input, paddingLeft: 24 }} type="number" placeholder="0.00" min="0" step="0.01"
                value={jobAmount} onChange={(e) => setJobAmount(e.target.value)} />
            </div>
          </Field>
          <Field label={`GST on Contractor Invoice (${(GST_RATE * 100).toFixed(0)}% auto-calculated)`}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ position: "relative", flex: 1 }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9993A8", fontSize: 13 }}>$</span>
                <input style={{ ...S.input, paddingLeft: 24 }} type="number" placeholder="0.00" min="0" step="0.01"
                  value={gstAmount} onChange={(e) => setGstAmount(e.target.value)} />
              </div>
              <button style={{ ...S.btnSmall, background: "#ffffff10", color: "#9993A8", fontSize: 11, whiteSpace: "nowrap" }}
                onClick={() => setGstAmount((parseFloat(jobAmount) * GST_RATE).toFixed(2))}>
                Auto ({(GST_RATE * 100).toFixed(0)}%)
              </button>
            </div>
          </Field>
          {contractor?.gstNumber && (
            <div style={{ fontSize: 11, color: "#5A5570", marginBottom: 8 }}>
              Contractor GST# {contractor.gstNumber}
            </div>
          )}
          <button style={{ ...S.btnSmall, background: "#E8622A", color: "#fff", width: "100%" }} onClick={handleAssign}>
            Save Assignment
          </button>
        </div>

        {/* Pay Contractor */}
        {(contractorId || ticket.contractorId) && (
          <div style={{ background: ticket.paid ? "#22AA5510" : "#0F0F14", border: `1px solid ${ticket.paid ? "#22AA5530" : "#ffffff08"}`, borderRadius: 10, padding: "14px", marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em", color: ticket.paid ? "#22AA55" : "#9993A8" }}>
              {ticket.paid ? "Payment Sent" : "Pay Contractor"}
            </div>
            {ticket.paid ? (
              <div style={{ fontSize: 13, color: "#9993A8" }}>
                {fmtC(ticket.jobAmount)} paid to {ticket.assignee}
              </div>
            ) : (
              <>
                {payResult === "success" && (
                  <div style={S.successBanner}>Payment recorded - send e-Transfer to contractor.</div>
                )}
                {!payResult && (
                  <>
                    {!canPay && (
                      <div style={{ fontSize: 12, color: "#5A5570", marginBottom: 10 }}>
                        {ticket.status !== "RESOLVED" && <div>Mark ticket Resolved first</div>}
                        {!contractor && <div>Select a contractor above</div>}
                        {contractor && !jobAmount && <div>Enter invoice amount above</div>}
                      </div>
                    )}
                    <button
                      style={{ ...S.btnSmall, background: canPay ? "#22AA55" : "#22AA5544", color: canPay ? "#fff" : "#22AA5588", width: "100%", cursor: canPay ? "pointer" : "not-allowed" }}
                      onClick={handlePay} disabled={!canPay}>
                      {`Mark Paid via e-Transfer${jobAmount ? ` - ${fmtC(parseFloat(jobAmount))}` : ""}`}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Billing + Markup */}
        {baseAmount > 0 && (
          <div style={{ background: "#0F0F14", border: "1px solid #F5C84222", borderRadius: 10, padding: "14px", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#F5C842" }}>
                Billing + Markup
              </div>
              <button style={{ ...S.btnSmall, background: "#ffffff10", color: "#9993A8", fontSize: 11 }}
                onClick={() => setShowBilling((v) => !v)}>
                {showBilling ? "Collapse" : "Expand"}
              </button>
            </div>

            {!showBilling && (
              <div style={{ fontSize: 12, color: billed ? "#F5C842" : "#5A5570", marginTop: 8 }}>
                {billed
                  ? `${fmtC(billableTotal)} billed to ${billableTo}`
                  : `${fmtC(baseAmount)} contractor invoice - add markup and bill owner or renter`}
              </div>
            )}

            {showBilling && (
              <div style={{ marginTop: 14 }}>
                <Field label="Your Markup %">
                  <div style={{ display: "flex", gap: 6 }}>
                    {[10, 15, 20, 25].map((pct) => (
                      <button key={pct}
                        style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: `1px solid ${markupPct === pct ? "#E8622A" : "#ffffff15"}`, background: markupPct === pct ? "#E8622A18" : "transparent", color: markupPct === pct ? "#E8622A" : "#9993A8", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                        onClick={() => setMarkupPct(pct)}>{pct}%</button>
                    ))}
                    <input style={{ ...S.input, width: 60, textAlign: "center", fontSize: 12 }} type="number"
                      value={markupPct} onChange={(e) => setMarkupPct(parseFloat(e.target.value) || 0)} />
                  </div>
                </Field>

                <Field label="Bill To">
                  <div style={{ display: "flex", gap: 8 }}>
                    {[
                      ["owner",  `Owner${owner ? ` (${owner.name})` : ""}`],
                      ["renter", `Renter${ticket.renter ? ` (${ticket.renter})` : ""}`],
                    ].map(([val, label]) => (
                      <button key={val}
                        style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: `1px solid ${billableTo === val ? "#2A7BE8" : "#ffffff15"}`, background: billableTo === val ? "#2A7BE818" : "transparent", color: billableTo === val ? "#2A7BE8" : "#9993A8", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                        onClick={() => setBillableTo(val)}>{label}</button>
                    ))}
                  </div>
                </Field>

                <Field label="Note (optional)">
                  <input style={S.input} placeholder="e.g. Damage caused by tenant" value={billNote}
                    onChange={(e) => setBillNote(e.target.value)} />
                </Field>

                {/* Summary */}
                <div style={{ background: "#141418", borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
                  {[
                    ["Contractor Invoice",    fmtC(baseAmount)],
                    ["GST (contractor)",      fmtC(gst)],
                    [`Markup (${markupPct}%)`,fmtC(markupAmt)],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9993A8", marginBottom: 4 }}>
                      <span>{label}</span><span>{val}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 800, color: "#F0EDE8", borderTop: "1px solid #ffffff10", paddingTop: 8, marginTop: 4 }}>
                    <span>Total Billable</span>
                    <span style={{ color: "#E8622A" }}>{fmtC(billableTotal)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#5A5570", marginTop: 4 }}>
                    Bill to: {billableTo === "owner" ? (owner?.name || "Property Owner") : (ticket.renter || "Tenant")}
                  </div>
                </div>

                <button
                  style={{ ...S.btnPrimary, width: "100%", fontSize: 13, background: billed ? "#22AA55" : "#E8622A" }}
                  onClick={handleGenerateBill}>
                  {billed ? "Billed - Copy Again" : `Generate Bill - ${fmtC(billableTotal)}`}
                </button>

                {billed && (
                  <div style={{ fontSize: 11, color: "#9993A8", marginTop: 8, textAlign: "center" }}>
                    {billableTo === "owner"
                      ? "Add as line item in Invoicing tab"
                      : "Send notice to renter via BC Forms tab"}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Status Updater */}
        <div style={S.label}>Update Status</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["OPEN", "IN_PROGRESS", "RESOLVED"].map((s) => {
            const st = STATUS_STYLES[s];
            const isActive = ticket.status === s;
            return (
              <button key={s}
                style={{ ...S.btnSmall, background: isActive ? st.bg : "#ffffff08", color: isActive ? st.text : "#9993A8", border: `1px solid ${isActive ? st.text + "44" : "transparent"}` }}
                onClick={() => onUpdateStatus(ticket.id, s)}>
                {st.label}
              </button>
            );
          })}
        </div>

      {/* -- COMMENTS THREAD -- */}
      <div style={{ marginTop: 24, borderTop: "1px solid #ffffff08", paddingTop: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#5A5570", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Updates & Notes ({comments.length})
        </div>

        {/* Comment thread */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
          {comments.length === 0 && (
            <div style={{ fontSize: 12, color: "#5A5570", fontStyle: "italic", padding: "12px 0" }}>
              No updates yet. Add the first note below.
            </div>
          )}
          {comments.map((c) => (
            <div key={c.id} style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 24, height: 24, borderRadius: 99, background: "#E8622A22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#E8622A", fontWeight: 800 }}>
                    {c.authorName?.[0] || "M"}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#F0EDE8" }}>{c.authorName}</span>
                  <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 99, background: "#2A7BE818", color: "#2A7BE8", fontWeight: 600 }}>
                    {c.authorRole === "manager" ? "Manager" : "Renter"}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "#5A5570" }}>
                  {new Date(c.createdAt).toLocaleDateString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
              <div style={{ fontSize: 13, color: "#D0CDD8", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{c.message}</div>
            </div>
          ))}
        </div>

        {/* New comment input */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <textarea
            style={{ ...S.input, minHeight: 80, resize: "vertical", fontSize: 13 }}
            placeholder="Add an update, note, or resolution detail..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
          <button
            style={{ ...S.btnPrimary, fontSize: 12, padding: "9px 18px", alignSelf: "flex-end", opacity: postingComment ? 0.6 : 1 }}
            disabled={postingComment || !newComment.trim()}
            onClick={async () => {
              if (!newComment.trim()) return;
              setPostingComment(true);
              const saved = await onAddComment(ticket.id, newComment.trim(), currentUser?.name || "Manager");
              if (saved) {
                setComments((prev) => [...prev, saved]);
                setNewComment("");
                // Also email renter if they have an email
                if (renterEmail) {
                  const { sendResolutionEmail } = await import("../utils/emailService");
                  // Reuse resolution email template with comment as body
                  fetch(`mailto:${renterEmail}`); // simple notification
                }
              }
              setPostingComment(false);
            }}>
            {postingComment ? "Posting..." : "Post Update"}
          </button>
        </div>
      </div>

      </div>
    </div>
  );
};

export default TicketDrawer;
